import React, { useState, useEffect } from "react";
import axios from "axios";
import { X, Trophy } from "lucide-react";
import { toast } from "sonner";
import TestimonialCarousel from "../components/TestimonialCarousel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LandingPage() {
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [waitlistCount, setWaitlistCount] = useState(400);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [activeMockup, setActiveMockup] = useState(0);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    primary_location: "",
    climbing_grade: "",
    biggest_frustration: "",
    willing_to_pay: "",
    travel_frequency: ""
  });

  useEffect(() => {
    fetchWaitlistCount();
    
    // Ensure video plays
    const video = document.querySelector('.hero-video');
    if (video) {
      video.play().catch(err => console.log('Video autoplay prevented:', err));
    }
  }, []);

  const fetchWaitlistCount = async () => {
    try {
      const response = await axios.get(`${API}/waitlist/count`);
      setWaitlistCount(400 + response.data.count);
    } catch (e) {
      console.error("Error fetching waitlist count:", e);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.primary_location || 
        !formData.climbing_grade || !formData.biggest_frustration || 
        !formData.willing_to_pay || !formData.travel_frequency) {
      toast.error("Please complete all fields");
      return;
    }

    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/waitlist`, formData);
      setShowThankYou(true);
      fetchWaitlistCount();
      toast.success("Welcome to the SendHer waitlist!");
      
      // Trigger Google Analytics generate_lead event
      if (window.gtag) {
        window.gtag('event', 'generate_lead', {
          currency: 'USD',
          value: 20.0,
          lead_source: 'waitlist',
          climbing_grade: formData.climbing_grade,
          willing_to_pay: formData.willing_to_pay
        });
      }
      
      // Trigger Meta Pixel Lead event
      if (window.fbq) {
        window.fbq('track', 'Lead', {
          content_name: 'SendHer Waitlist',
          content_category: 'Climbing Partner App',
          value: 20.0,
          currency: 'USD',
          predicted_ltv: 20.0
        });
      }
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("This email is already registered");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const openWaitlistModal = () => {
    setShowWaitlistModal(true);
    
    // Track modal open in Google Analytics
    if (window.gtag) {
      window.gtag('event', 'begin_checkout', {
        items: [{
          item_name: 'SendHer Waitlist',
          item_category: 'Waitlist',
        }]
      });
    }
    
    // Track in Meta Pixel
    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        content_name: 'SendHer Waitlist Form',
        content_category: 'Waitlist'
      });
    }
  };

  const closeModal = () => {
    setShowWaitlistModal(false);
    setShowThankYou(false);
    setFormData({
      name: "",
      email: "",
      primary_location: "",
      climbing_grade: "",
      biggest_frustration: "",
      willing_to_pay: "",
      travel_frequency: ""
    });
  };

  return (
    <div className="landing-page">
      {/* Hero Section with Image */}
      <section className="hero-section">
        <div className="hero-image-container">
          <img 
            className="hero-image" 
            src="https://customer-assets.emergentagent.com/job_66ed4fb3-cf13-4598-ac2a-7698690c49be/artifacts/qqdie9rd_AdobeStock_60373494.jpeg"
            alt="Woman rock climbing on overhang with ocean view"
          />
          <div className="hero-video-overlay"></div>
        </div>
        <div className="hero-content">
          <div className="hero-text">
            <h1 data-testid="hero-headline">Stop Auto-Belaying.<br />Find Partners for New Climb Adventures.</h1>
            <p data-testid="hero-subheadline" className="hero-subheadline">
              The first climbing partner app designed by women, for women 
              to find verified partners who match your grade and 
              know how to catch a whipper.
            </p>
            <button 
              data-testid="hero-cta-button"
              className="cta-primary" 
              onClick={openWaitlistModal}
            >
              Join Waitlist for Early Access
            </button>
            <p className="trust-signal" data-testid="trust-signal">
              Join {waitlistCount}+ climbers already on the vetted waitlist.
            </p>
          </div>
        </div>
      </section>

      {/* Value Prop Section */}
      <section className="value-prop-section">
        <h2 data-testid="value-prop-header">Why SendHer?</h2>
        <div className="feature-grid">
          <div className="feature-card" data-testid="feature-identity">
            <div className="feature-image-container">
              <img 
                src="https://customer-assets.emergentagent.com/job_66ed4fb3-cf13-4598-ac2a-7698690c49be/artifacts/x90s6ad1_AdobeStock_928384660.jpeg" 
                alt="Woman climbing with harness and safety gear"
                className="feature-image"
              />
            </div>
            <div className="feature-content">
              <h3>ID-Verified Community</h3>
              <p>
                Every member is background-checked and identity-verified. 
                Safety isn't an "extra"—it's our baseline.
              </p>
            </div>
          </div>
          
          <div className="feature-card" data-testid="feature-technical">
            <div className="feature-image-container">
              <img 
                src="https://images.unsplash.com/photo-1682871984586-a16c1e4a45ab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHx3b21hbiUyMHJvY2slMjBjbGltYmluZyUyMGhhcm5lc3MlMjBzYWZldHklMjBnZWFyJTIwb3V0ZG9vcnxlbnwwfHx8fDE3NzE4NjgyNTh8MA&ixlib=rb-4.1.0&q=85" 
                alt="Woman lead climbing outdoors with gear"
                className="feature-image"
              />
            </div>
            <div className="feature-content">
              <h3>Skill-Vetted Profiles</h3>
              <p>
                Filter by grade, multi-pitch experience, or trad proficiency. No more showing up to the crag with someone who can't clean an anchor.
              </p>
            </div>
          </div>
          
          <div className="feature-card" data-testid="feature-vouch">
            <div className="feature-image-container">
              <img 
                src="https://customer-assets.emergentagent.com/job_66ed4fb3-cf13-4598-ac2a-7698690c49be/artifacts/ucbo42ag_AdobeStock_60439707.jpeg" 
                alt="Woman climbing orange rock face with harness"
                className="feature-image"
              />
            </div>
            <div className="feature-content">
              <h3>The Vouch System</h3>
              <p>
                Earn your "Trusted Partner" badge. Every successful day out earns you 
                vouch points from your partner. 3 vouches = Verified.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Vouch System Explanation - Orange Background */}
      <section className="vouch-explanation-section">
        <div className="vouch-content">
          <h2 data-testid="vouch-header">Trust is Earned on the Wall.</h2>
          <p className="vouch-intro">
            We solve the "Reliability Gap" so that you can find a partner that matches your style and commitment level.
          </p>
          <div className="vouch-details">
            <div className="vouch-item">
              <div className="vouch-badge">✓</div>
              <div>
                <h4>Trusted Partner Badge</h4>
                <p>Awarded after 3 unique vouches</p>
              </div>
            </div>
            <div className="vouch-item">
              <div className="vouch-badge">★</div>
              <div>
                <h4>Reviews</h4>
                <p>Read detailed feedback from previous climbing sessions</p>
              </div>
            </div>
            <div className="vouch-item">
              <div className="vouch-badge"><Trophy size={24} strokeWidth={2.5} /></div>
              <div>
                <h4>Perks</h4>
                <p>Trusted partners get priority matching and Pro status for life</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* App Mockup Showcase */}
      <section className="mockup-section">
        <h2 data-testid="mockup-header">See How It Works</h2>
        <div 
          className="mockup-grid"
          onScroll={(e) => {
            const container = e.target;
            const scrollLeft = container.scrollLeft;
            const itemWidth = container.firstChild?.offsetWidth || 280;
            const gap = 16;
            const newIndex = Math.round(scrollLeft / (itemWidth + gap));
            if (newIndex !== activeMockup && newIndex >= 0 && newIndex < 3) {
              setActiveMockup(newIndex);
            }
          }}
        >
          <div className="mockup-item" data-testid="mockup-profile">
            <img 
              src="https://customer-assets.emergentagent.com/job_66ed4fb3-cf13-4598-ac2a-7698690c49be/artifacts/8cwnxsxi_91a2954ba486a4913f18a2422e620355ccccdb9f08de0310fb0fb08761c33bd9.png" 
              alt="SendHer app profile screen"
              className="mockup-phone"
            />
            <p className="mockup-caption">DETAILED SKILL-VERIFIED PROFILES</p>
          </div>
          <div className="mockup-item" data-testid="mockup-matching">
            <img 
              src="https://customer-assets.emergentagent.com/job_66ed4fb3-cf13-4598-ac2a-7698690c49be/artifacts/j2qh3loj_a4ad10eec631dfefa3ee1faac1cd903d47fba28b4d338928bd62ebb2e1a19697.png" 
              alt="SendHer app matching screen"
              className="mockup-phone"
            />
            <p className="mockup-caption">FIND PARTNERS BY GRADE & LOCATION</p>
          </div>
          <div className="mockup-item" data-testid="mockup-vouch">
            <img 
              src="https://customer-assets.emergentagent.com/job_66ed4fb3-cf13-4598-ac2a-7698690c49be/artifacts/31d7ouf8_0a0be7f2ec9dabbc45be1a8eb37afa40613c1adc446eaf24f88a2811a9e281be.png" 
              alt="SendHer app vouch screen"
              className="mockup-phone"
            />
            <p className="mockup-caption">POST-CLIMB SAFETY & VIBES CHECK</p>
          </div>
        </div>
        {/* Mobile Pagination Dots */}
        <div className="mockup-pagination" data-testid="mockup-pagination">
          {[0, 1, 2].map((index) => (
            <button
              key={index}
              className={`dot ${index === activeMockup ? 'active' : ''}`}
              aria-label={`View mockup ${index + 1}`}
              data-testid={`mockup-dot-${index}`}
            />
          ))}
        </div>
      </section>

      {/* Testimonials Carousel */}
      <TestimonialCarousel />

      {/* Final CTA Section */}
      <section className="final-cta-section">
        <h2 data-testid="final-cta-headline">Summer 2026 is for Sending.</h2>
        <p className="final-cta-copy">
          We launch in April. Secure your spot with the first 500 members to get your first year subscription free!
        </p>
        <button 
          data-testid="final-cta-button"
          className="cta-primary" 
          onClick={openWaitlistModal}
        >
          Join the Waitlist
        </button>
      </section>

      {/* Footer */}
      <footer className="footer">
        <p>Launching April 2026. Built by women for the send.</p>
      </footer>

      {/* Waitlist Modal */}
      {showWaitlistModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} data-testid="waitlist-modal">
            <button className="modal-close" onClick={closeModal} data-testid="modal-close-button">
              <X size={24} />
            </button>
            
            {!showThankYou ? (
              <>
                <h2 className="modal-title">Join the Waitlist</h2>
                <p className="modal-subtitle">Secure your spot for early access in April 2026</p>
                
                <form onSubmit={handleSubmit} className="waitlist-form">
                  <div className="form-group">
                    <label htmlFor="name">Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      data-testid="input-name"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      data-testid="input-email"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="primary_location">Primary Gym / Location</label>
                    <input
                      type="text"
                      id="primary_location"
                      name="primary_location"
                      value={formData.primary_location}
                      onChange={handleInputChange}
                      placeholder="e.g., Boulder Rock Club, Red River Gorge"
                      required
                      data-testid="input-location"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>What is your current Lead Climbing grade?</label>
                    <div className="radio-group">
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="climbing_grade"
                          value="5.9 or below"
                          onChange={handleInputChange}
                          data-testid="grade-5.9"
                        />
                        5.9 or below
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="climbing_grade"
                          value="5.10"
                          onChange={handleInputChange}
                          data-testid="grade-5.10"
                        />
                        5.10
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="climbing_grade"
                          value="5.11"
                          onChange={handleInputChange}
                          data-testid="grade-5.11"
                        />
                        5.11
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="climbing_grade"
                          value="5.12+"
                          onChange={handleInputChange}
                          data-testid="grade-5.12+"
                        />
                        5.12+
                      </label>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label>What is your biggest frustration with finding partners?</label>
                    <div className="radio-group">
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="biggest_frustration"
                          value="Safety/Creeps"
                          onChange={handleInputChange}
                          data-testid="frustration-safety"
                        />
                        Safety/Creeps
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="biggest_frustration"
                          value="Inconsistent skill levels"
                          onChange={handleInputChange}
                          data-testid="frustration-skills"
                        />
                        Inconsistent skill levels
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="biggest_frustration"
                          value="Friends keep canceling"
                          onChange={handleInputChange}
                          data-testid="frustration-canceling"
                        />
                        Friends keep canceling
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="biggest_frustration"
                          value="New to the area"
                          onChange={handleInputChange}
                          data-testid="frustration-new"
                        />
                        New to the area
                      </label>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label>Would you pay a small one-time fee ($15–$20) for lifetime ID-verification?</label>
                    <div className="radio-group">
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="willing_to_pay"
                          value="Yes, safety is worth it"
                          onChange={handleInputChange}
                          data-testid="payment-yes"
                        />
                        Yes, safety is worth it
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="willing_to_pay"
                          value="Maybe, depends on the app"
                          onChange={handleInputChange}
                          data-testid="payment-maybe"
                        />
                        Maybe, depends on the app
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="willing_to_pay"
                          value="No, I want it free"
                          onChange={handleInputChange}
                          data-testid="payment-no"
                        />
                        No, I want it free
                      </label>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label>How often do you travel specifically to climb?</label>
                    <div className="radio-group">
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="travel_frequency"
                          value="Every month"
                          onChange={handleInputChange}
                          data-testid="travel-monthly"
                        />
                        Every month
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="travel_frequency"
                          value="2-3 times a year"
                          onChange={handleInputChange}
                          data-testid="travel-yearly"
                        />
                        2-3 times a year
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="travel_frequency"
                          value="Rarely"
                          onChange={handleInputChange}
                          data-testid="travel-rarely"
                        />
                        Rarely
                      </label>
                    </div>
                  </div>
                  
                  <button 
                    type="submit" 
                    className="cta-primary submit-button"
                    disabled={isSubmitting}
                    data-testid="submit-waitlist-button"
                  >
                    {isSubmitting ? "Submitting..." : "Secure My Spot"}
                  </button>
                </form>
              </>
            ) : (
              <div className="thank-you-content" data-testid="thank-you-message">
                <div className="thank-you-icon">✓</div>
                <h2>You're on the List!</h2>
                <p>
                  Welcome to the SendHer community. We'll notify you when we launch in April 2026.
                </p>
                <p className="founder-badge">
                  🏆 You've secured your Founder Badge
                </p>
                <button className="cta-primary" onClick={closeModal} data-testid="close-thank-you-button">
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
